// src/components/modals/AuthModal.tsx
import { createPortal } from 'react-dom';
import { useEffect, useState } from 'react';
import styled from 'styled-components';
import {
  getMultiFactorResolver,
  signInWithEmailAndPassword,
  sendEmailVerification,
  signOut,
  TotpMultiFactorGenerator,
} from 'firebase/auth';
import { useNavigate } from 'react-router-dom';
import { auth } from '../../lib/firebase';
import { useAuth } from '../../context/AuthProvider';
import Input from '../Input';
import {
  cyberGlassBackdrop,
  cyberGlassHairline,
  cyberGlassSurface,
  cyberGlassTextMuted,
  cyberGlassTextPrimary,
} from '../../styles/cyberGlass';
import { gradientPillCss } from '../../styles/gradientPill';

const API_BASE = (process.env.REACT_APP_API_BASE ?? '').trim();

interface Props {
  open: boolean;
  onClose: () => void;
  initialEmail?: string;
}

type ModalView = 'login' | 'forgotPassword';

/* ────────────────── UI ────────────────── */
const Backdrop = styled.div`
  position: fixed; inset: 0; z-index: 60;
  display: grid; place-items: center;
  ${cyberGlassBackdrop};

  @media (max-width: 640px) {
    place-items: stretch;
  }
`;
const Card = styled.div`
  position: relative; width: 100%; max-width: 420px;
  padding: 22px; border-radius: 16px;
  color: ${cyberGlassTextPrimary};
  ${cyberGlassSurface};

  @media (max-width: 640px) {
    max-width: none;
    min-height: 100dvh;
    min-height: 100vh;
    border-radius: 0;
    padding:
      calc(env(safe-area-inset-top) + 1rem)
      1rem
      calc(env(safe-area-inset-bottom) + 1rem);
    display: flex;
    flex-direction: column;
    justify-content: center;
    overflow-y: auto;
  }
`;
const CloseX = styled.button`
  position: absolute; top: 10px; right: 10px;
  width: 36px; height: 36px; border-radius: 10px;
  border: 1px solid ${cyberGlassHairline};
  background: rgba(9,14,34,.46); color: ${cyberGlassTextPrimary};
  display: grid; place-items: center; font-size: 18px; cursor: pointer;
  z-index: 3;
  touch-action: manipulation;
  &:hover { background: rgba(18, 28, 64, .64); }

  @media (max-width: 640px) {
    top: calc(env(safe-area-inset-top) + 0.85rem);
    right: 1rem;
  }
`;
const Title = styled.h3`
  margin: 0 0 14px; font-size: 22px; font-weight: 800;
`;
const CopyText = styled.p`
  margin: -6px 0 0;
  color: ${cyberGlassTextMuted};
  font-size: 12px;
  line-height: 1.55;
`;
const ErrorText = styled.p.attrs({ role: 'alert', 'aria-live': 'assertive' })`
  color: #ff6b6b; font-size: 12px; margin: -4px 0 0;
`;
const InfoText = styled.p.attrs({ role: 'status', 'aria-live': 'polite' })`
  color: #9fe8ff; font-size: 12px; margin: 8px 0 0;
`;

const Legal = styled.p`
  margin: 12px 2px 0; color: ${cyberGlassTextMuted}; font-size: 11px; text-align: center;
`;

const ActionsRow = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const LinkButton = styled.button`
  border: 0;
  background: transparent;
  color: #9fd7ff;
  font-size: 12px;
  padding: 0;
  cursor: pointer;
  text-decoration: underline;
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;




/* Pill gradient primary CTA (cyan → peach) */
const PrimaryCta = styled.button<{ disabled?: boolean }>`
  width: 100%;
  text-transform: uppercase;
  ${gradientPillCss};
`;

const ModalContent = styled.div`
  display: grid;
  gap: 0.9rem;
  width: 100%;
  max-width: 100%;

  @media (max-width: 640px) {
    width: min(100%, 28rem);
    margin: auto;
    padding-top: 2.5rem;
  }
`;

const AuthInput = styled(Input)`
  background: transparent;
  color: ${cyberGlassTextPrimary};
  border: 1px dashed ${cyberGlassHairline};
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.05);

  &::placeholder {
    color: ${cyberGlassTextMuted};
  }

  &:focus {
    outline: none;
    border-color: rgba(159, 215, 255, 0.7);
    box-shadow:
      inset 0 1px 0 rgba(255, 255, 255, 0.05),
      0 0 0 3px rgba(141, 235, 255, 0.12);
  }
`;


export default function AuthModal({ open, onClose, initialEmail = '' }: Props) {
  const [view, setView] = useState<ModalView>('login');
  const [email, setEmail] = useState('');
  const [pass, setPass] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [mfaResolver, setMfaResolver] = useState<any | null>(null);
  const [mfaEnrollmentId, setMfaEnrollmentId] = useState<string | null>(null);
  const [mfaHintLabel, setMfaHintLabel] = useState<string>('');
  const [err, setErr] = useState('');
  const [info, setInfo] = useState('');
  const [loading, setLoading] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const [mfaLoading, setMfaLoading] = useState(false);

  const { refreshRole } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!open) return;
    const onEsc = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onEsc);
    return () => window.removeEventListener('keydown', onEsc);
  }, [open, onClose]);

  useEffect(() => {
    if (!open) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    if (initialEmail?.trim()) {
      setEmail(initialEmail.trim().toLowerCase());
    }
    setView('login');
    setPass('');
    setMfaCode('');
    setMfaResolver(null);
    setMfaEnrollmentId(null);
    setMfaHintLabel('');
    setErr('');
    setInfo('');
  }, [open, initialEmail]);

  if (!open) return null;


  const acs = {
    url: `${window.location.origin}/verify-email`,
    handleCodeInApp: true as const,
  };


  /* ───────── helpers ───────── */
  const ensureUserDoc = async () => {
    const current = auth.currentUser;
    if (!current) return;
    const token = await current.getIdToken(true);
    await fetch(`${API_BASE}/api/users`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
    });
  };




  const routeByRole = async () => {
    const current = auth.currentUser;
    if (!current) return;

    const token = await current.getIdToken(true);
    const meRes = await fetch(`${API_BASE}/api/users/me`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const meData = await meRes.json().catch(() => null);
    const isBanned =
      meRes.status === 403 ||
      Boolean(meData?.banned) ||
      /banned|locked|disabled/i.test(
        [meData?.code, meData?.error, meData?.message].filter(Boolean).join(' '),
      );

    if (isBanned) {
      await signOut(auth);
      onClose();
      navigate('/account-locked', { replace: true });
      return;
    }

    const tokenResult = await auth.currentUser?.getIdTokenResult(true);
    const roleClaim = (tokenResult?.claims?.role as string | undefined) ?? null;
    await refreshRole();
    onClose();

    if (roleClaim === 'admin') {
      navigate('/dashboard/overview', { replace: true });
      return;
    }
    if (roleClaim === 'agent') {
      navigate('/dashboard/list', { replace: true });
      return;
    }
    if (roleClaim === 'candidate') {
      navigate('/careers', { replace: true });
      return;
    }
    if (roleClaim === 'client') {
      navigate('/dashboard/home', { replace: true });
      return;
    }

    navigate('/', { replace: true });
  };


  /* ───────── actions ───────── */
  const handleForgotPassword = async () => {
    if (loading || resetLoading) return;
    const normalized = email.trim().toLowerCase();
    if (!normalized) {
      setErr('Enter the email for your account.');
      return;
    }
    if (!/\S+@\S+\.\S+/.test(normalized)) {
      setErr('Please enter a valid email address.');
      return;
    }

    setErr('');
    setInfo('');
    setResetLoading(true);
    try {
      const response = await fetch(`${API_BASE}/api/auth/password-reset`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: normalized }),
      });
      if (!response.ok) {
        const data = await response.json().catch(() => null);
        throw new Error(data?.message || 'Could not send password reset email.');
      }
      setInfo(`If an account exists for ${normalized}, a password reset email has been sent.`);
    } catch (e: any) {
      setErr((e?.message || '').toString() || 'Could not send password reset email.');
    } finally {
      setResetLoading(false);
    }
  };

  const openForgotPasswordView = () => {
    if (loading || resetLoading || mfaLoading) return;
    setErr('');
    setInfo('');
    setView('forgotPassword');
  };

  const returnToLoginView = () => {
    if (loading || resetLoading || mfaLoading) return;
    setErr('');
    setInfo('');
    setView('login');
  };

  const handleEmailPassword = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (loading || mfaLoading) return;
    setErr(''); setInfo(''); setLoading(true);
    const normalized = email.trim().toLowerCase();
    try {
      // sign-in path
      const { user } = await signInWithEmailAndPassword(auth, normalized, pass);


      // Keep verification email optional/non-blocking for invited or newly-created users.
      if (!user.emailVerified) {
        try { await sendEmailVerification(user, acs); } catch { }
      }

      await ensureUserDoc();
      await routeByRole();
    } catch (e: any) {
      const isMfaChallenge = String(e?.code ?? '') === 'auth/multi-factor-auth-required';
      if (isMfaChallenge) {
        try {
          const resolver = getMultiFactorResolver(auth, e);
          const totpHint = resolver.hints.find(
            (hint) => (hint as any).factorId === TotpMultiFactorGenerator.FACTOR_ID,
          );
          if (!totpHint) {
            setErr(
              'This account requires a second factor method that is not supported in this sign-in form yet.',
            );
            return;
          }
          setMfaResolver(resolver);
          setMfaEnrollmentId(totpHint.uid);
          setMfaHintLabel(totpHint.displayName || 'Authenticator app');
          setPass('');
          setInfo(`Enter the 6-digit code from ${totpHint.displayName || 'your authenticator app'}.`);
          return;
        } catch (resolverError: any) {
          setErr(friendlyAuthMessage(resolverError));
          return;
        }
      }
      setErr(friendlyAuthMessage(e));
    } finally {
      setLoading(false);
    }
  };

  const handleMfaSignIn = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (mfaLoading || loading) return;
    if (!mfaResolver || !mfaEnrollmentId) {
      setErr('Second-factor challenge expired. Please log in again.');
      setMfaResolver(null);
      setMfaEnrollmentId(null);
      return;
    }
    const otp = mfaCode.trim();
    if (otp.length < 6) {
      setErr('Enter the 6-digit code from your authenticator app.');
      return;
    }

    setErr('');
    setInfo('');
    setMfaLoading(true);
    try {
      const assertion = TotpMultiFactorGenerator.assertionForSignIn(mfaEnrollmentId, otp);
      await mfaResolver.resolveSignIn(assertion);
      await ensureUserDoc();
      await routeByRole();
    } catch (e: any) {
      setErr(friendlyAuthMessage(e));
    } finally {
      setMfaLoading(false);
    }
  };

  const resetMfaChallenge = () => {
    if (mfaLoading) return;
    setMfaResolver(null);
    setMfaEnrollmentId(null);
    setMfaHintLabel('');
    setMfaCode('');
    setErr('');
    setInfo('');
  };

  return createPortal(
    <Backdrop onClick={() => !loading && !resetLoading && onClose()}>
      <Card
        id="auth-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="auth-title"
        onClick={e => e.stopPropagation()}
      >
        <CloseX
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            onClose();
          }}
          aria-label="Close"
        >
          X
        </CloseX>

        <ModalContent>
          <Title id="auth-title">
            {mfaResolver
              ? 'Two-step verification'
              : view === 'forgotPassword'
                ? 'Reset your password'
                : 'Welcome back'}
          </Title>
          {view === 'forgotPassword' && !mfaResolver ? (
            <CopyText>
              Confirm the email for your account and we will send a secure reset link if it
              exists.
            </CopyText>
          ) : null}

          {mfaResolver ? (
            <form onSubmit={handleMfaSignIn} style={{ display: 'grid', gap: 10 }}>
              <AuthInput
                placeholder="6-digit authenticator code"
                type="text"
                inputMode="numeric"
                value={mfaCode}
                onChange={e => setMfaCode(e.target.value)}
                required
                autoComplete="one-time-code"
              />
              <ActionsRow>
                <LinkButton type="button" onClick={resetMfaChallenge} disabled={mfaLoading}>
                  Use different account
                </LinkButton>
              </ActionsRow>

              {err && <ErrorText>{err}</ErrorText>}
              {info && <InfoText>{info}</InfoText>}

              <PrimaryCta type="submit" disabled={mfaLoading} aria-busy={mfaLoading}>
                {mfaLoading ? 'Verifying code...' : `Verify ${mfaHintLabel || 'code'}`}
              </PrimaryCta>
            </form>
          ) : view === 'forgotPassword' ? (
            <form
              onSubmit={(event) => {
                event.preventDefault();
                void handleForgotPassword();
              }}
              style={{ display: 'grid', gap: 10 }}
            >
              <AuthInput
                placeholder="Email"
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                required
                autoComplete="email"
              />
              <ActionsRow>
                <LinkButton
                  type="button"
                  onClick={returnToLoginView}
                  disabled={resetLoading}
                >
                  Back to login
                </LinkButton>
              </ActionsRow>

              {err && <ErrorText>{err}</ErrorText>}
              {info && <InfoText>{info}</InfoText>}

              <PrimaryCta type="submit" disabled={resetLoading} aria-busy={resetLoading}>
                {resetLoading ? 'Sending reset email...' : 'Send reset link'}
              </PrimaryCta>
            </form>
          ) : (
            <form onSubmit={handleEmailPassword} style={{ display: 'grid', gap: 10 }}>
              <AuthInput
                placeholder="Email"
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                required
                autoComplete="email"
              />
              <AuthInput
                placeholder="Password"
                type="password"
                value={pass}
                onChange={e => setPass(e.target.value)}
                required
                autoComplete="current-password"
              />
              <ActionsRow>
                <LinkButton
                  type="button"
                  onClick={openForgotPasswordView}
                  disabled={loading || resetLoading}
                >
                  Forgot password?
                </LinkButton>
              </ActionsRow>

              {err && <ErrorText>{err}</ErrorText>}
              {info && <InfoText>{info}</InfoText>}

              <PrimaryCta type="submit" disabled={loading} aria-busy={loading}>
                Login
              </PrimaryCta>
            </form>
          )}
          <Legal>Accounts are provisioned by invite or after purchasing a service.</Legal>
        </ModalContent>
      </Card>
    </Backdrop>,
    document.body
  );
}

/* ───────── errors ───────── */
function friendlyAuthMessage(e: any): string {
  const code = (e?.code || '').toString();
  const msg = (e?.message || '').toString();
  switch (code) {
    case 'auth/invalid-credential':
    case 'auth/wrong-password':
      return 'Invalid email or password.';
    case 'auth/invalid-email':
      return 'Please enter a valid email address.';
    case 'auth/user-not-found':
      return 'No account exists with that email.';
    case 'auth/too-many-requests':
      return 'Too many attempts. Please try again later.';
    case 'auth/invalid-verification-code':
      return 'The authenticator code is invalid. Please try again.';
    case 'auth/code-expired':
      return 'That code expired. Enter a fresh code from your authenticator app.';
    case 'auth/missing-verification-code':
      return 'Enter the code from your authenticator app.';
    case 'auth/popup-closed-by-user':
      return 'Popup closed before completing sign in.';
    case 'auth/popup-blocked':
      return 'Your browser blocked the sign-in popup.';
    case 'auth/email-already-in-use':
      return 'An account with that email already exists.';
    case 'auth/weak-password':
      return 'Password is too weak.';
    default:
      return msg || 'Authentication error. Please try again.';
  }
}


